// Pure section-building for the Catalog list: filter the exercise catalog
// by search query + region + equipment, bucket the matches by muscle
// region in schema-enum order, sort each bucket by canonical EN name,
// and apply the collapse state. Lifted to @heraion/ui (Catalog surface
// lift) — the filtering/grouping logic renders identically on mobile + the
// web-rnw console and stays unit-testable without a render runner.
import {
  MuscleRegion,
  exerciseRegion,
  rankByMatch,
  type EquipmentKind as EquipmentKindT,
  type Exercise,
  type MuscleRegion as MuscleRegionT,
} from "@heraion/shared";

// Group order mirrors the web's catalog: schema enum order first, then
// "other" for legacy / region-less rows. Keeping the keys identical to
// the web means future surfacing of stored "user collapsed X group"
// preferences can sync across surfaces.
export const REGION_ORDER: ReadonlyArray<MuscleRegionT | "other"> = [
  ...MuscleRegion.options,
  "other",
];

export type Section = {
  key: MuscleRegionT | "other";
  data: Exercise[];
};

export function computeCatalogSections(args: {
  items: Exercise[] | null;
  query: string;
  regions: Set<MuscleRegionT>;
  equipment: Set<EquipmentKindT>;
  collapsed: Set<string>;
  /** UI locale: ranks a name in the reader's language above other matches. */
  locale?: string;
}): Section[] {
  const { items, query, regions, equipment, collapsed, locale } = args;
  if (items === null) return [];
  const q = query.trim();
  const filtersActive =
    q.length > 0 || regions.size > 0 || equipment.size > 0;

  const matches = items
    .filter((ex) => {
      if (regions.size === 0) return true;
      // Region is rarely stored on the exercise row; derive it from
      // the primary muscle so the chip filter actually narrows.
      const region = exerciseRegion(ex);
      return region ? regions.has(region) : false;
    })
    .filter((ex) => {
      if (equipment.size === 0) return true;
      return ex.equipment ? equipment.has(ex.equipment) : false;
    });

  const byRegion = new Map<MuscleRegionT | "other", Exercise[]>();
  for (const ex of matches) {
    // Same derivation as the filter so the section a row appears in
    // matches the chip that picks it out. Without this, every row
    // bucketed as "other" because Exercise.region is unset.
    const key: MuscleRegionT | "other" = exerciseRegion(ex) ?? "other";
    const list = byRegion.get(key);
    if (list) list.push(ex);
    else byRegion.set(key, [ex]);
  }
  const out: Section[] = [];
  for (const region of REGION_ORDER) {
    // Search filters + ranks; ties sort on the canonical EN name so the
    // order stays stable when the user toggles language at runtime.
    const list = rankByMatch(byRegion.get(region) ?? [], q, locale, (a, b) =>
      a.name.en.localeCompare(b.name.en),
    );
    if (list.length === 0) continue;
    // When any filter is active force-expand so matches are visible
    // without an extra tap. Returning data: [] when collapsed keeps
    // the SectionList header rendering and the chevron in sync.
    const isCollapsed = !filtersActive && collapsed.has(region);
    out.push({ key: region, data: isCollapsed ? [] : list });
  }
  return out;
}
